console.log('Board finder script starting');

function getBoardFinderStorefrontAccessToken() {
  return (
    window.shopifyConfig?.boardFinderStorefrontAccessToken ||
    window.shopifyConfig?.storefrontAccessToken ||
    ''
  );
}

class BoardFinder {
  constructor() {
    if (!this.isBoardFinderPage()) {
      return;
    }

    this.lastFetchedProducts = null;
    this.initializeElements();

    if (this.elementsExist()) {
      // Initialize properties that can be set by URL
      this.selectedBoardStyles = [];
      this.initialVendors = [];
      this.currentView = 'grid'; // Default view
      this.isInitialLoadWithParams = false;

      // Set initial state from URL first. Returns true if filters are present.
      const hasFilters = this.initializeFromURL();

      this.attachEventListeners();
      this.initializeSwiper();
      this.currentPage = 1;
      this.isLoading = false;
      this.initializeSliderValues(); // Must be after initializeFromURL
      this.fetchRanges();

      // Add back the style mapping
      this.styleMetafieldMap = {
        Carving: 'piste_carving_value',
        Freestyle: 'freestyle_park_value',
        Puder: 'powder_value',
        'All Mountain': 'freeride_value',
      };

      this.viewButtons = document.querySelectorAll('.view-button');
      this.productsGrid = document.querySelector('.products-grid');
      this.productsList = document.querySelector('.products-list');

      this.initializeViewSwitcher(); // Must be after initializeFromURL to set active button
      
      // Initialize selected products from localStorage
      try {
        const savedSelections = localStorage.getItem('selectedBoardProducts');
        this.selectedProducts = new Set(savedSelections ? JSON.parse(savedSelections) : []);
      } catch (error) {
        this.selectedProducts = new Set();
      }

      // Update labels with translations
      this.updateLabels();

      this.listViewVariants = []; // Add this property to store flattened variants

      // Add default sort directions
      this.defaultSortDirections = {
        vendor: true, // true = ascending (A-Ö)
        name: true, // true = ascending (A-Ö)
        price: true, // true = ascending (Low-High)
        length: false, // false = descending (High-Low)
        width: false, // false = descending (High-Low)
        radius: false, // false = descending (High-Low)
        bootsize: false, // false = descending (High-Low)
        minlength: true, // true = ascending (Low-High)
        maxlength: false, // false = descending (High-Low)
        flex: false, // false = descending (High-Low)
        carving: false, // false = descending (High-Low)
        freeride: false, // false = descending (High-Low)
        powder: false, // false = descending (High-Low)
        freestyle: false, // false = descending (High-Low)
      };

      // Add new properties for sort state
      this.currentSortColumn = null;
      this.currentSortDirection = null;

      // Add a flag to track if we're loading all boards
      this.isLoadingAll = false;
      
      // Auto-run search if URL has parameters
      if (hasFilters) {
        this.isInitialLoadWithParams = true;
        this.findBoards();
      }
    }
  }

  isBoardFinderPage() {
    return document.querySelector('.board-finder') !== null;
  }

  initializeElements() {
    try {
      this.riderLengthInput = document.getElementById('rider-length');
      this.bootSizeInput = document.getElementById('boot-size');
      this.riderLengthValue = document.getElementById('rider-length-value');
      this.bootSizeValue = document.getElementById('boot-size-value');
      this.findBoardsButton = document.getElementById('find-boards');
      if (this.findBoardsButton) {
        this.findBoardsButton.textContent = 'Visa Snowboards';
      }
      this.productsGrid = document.querySelector('.products-grid');
      this.loadingOverlay = document.querySelector('.loading-overlay');

      // Update Swiper selectors to match your HTML
      this.boardStyleFilter = document.querySelector('.board-style-filter');
      this.swiperContainer = document.querySelector('.board-finder__swiper');
      this.swiperWrapper = document.querySelector('.board-finder__swiper-wrapper');

      // Add vendor filter container
      this.vendorFilter = document.querySelector('.vendor-filter');
      this.vendorWrapper = document.querySelector('.vendor-filter__wrapper');
    } catch (error) {
      // Error handling without console
    }
  }

  elementsExist() {
    return (
      this.riderLengthInput &&
      this.bootSizeInput &&
      this.riderLengthValue &&
      this.bootSizeValue &&
      this.findBoardsButton &&
      this.productsGrid &&
      this.loadingOverlay
    );
  }

  initializeSliderValues() {
    if (this.riderLengthInput && this.riderLengthValue) {
      const lengthContainer = document.createElement('div');
      lengthContainer.className = 'slider-value-container';

      const lengthSelect = document.createElement('select');
      lengthSelect.className = 'slider-value-select';
      // Updated range: 100-210cm
      for (let i = 100; i <= 210; i++) {
        const option = document.createElement('option');
        option.value = i;
        option.text = `${i} cm`;
        lengthSelect.appendChild(option);
      }
      lengthSelect.value = this.riderLengthInput.value;

      this.riderLengthInput.addEventListener('input', (e) => {
        lengthSelect.value = e.target.value;
      });
      this.riderLengthInput.addEventListener('change', () => {
        this.updateURLParams();
      });

      lengthSelect.addEventListener('change', (e) => {
        this.riderLengthInput.value = e.target.value;
        this.riderLengthInput.dispatchEvent(new Event('input'));
        this.updateURLParams();
      });

      lengthContainer.appendChild(lengthSelect);
      this.riderLengthValue.parentNode.replaceChild(lengthContainer, this.riderLengthValue);
    }

    if (this.bootSizeInput && this.bootSizeValue) {
      const bootContainer = document.createElement('div');
      bootContainer.className = 'slider-value-container';

      const bootSelect = document.createElement('select');
      bootSelect.className = 'slider-value-select';
      // Updated range: 30-50
      for (let i = 30; i <= 50; i += 0.5) {
        const option = document.createElement('option');
        option.value = i;
        option.text = `EU ${i}`;
        bootSelect.appendChild(option);
      }
      bootSelect.value = this.bootSizeInput.value;

      this.bootSizeInput.addEventListener('input', (e) => {
        bootSelect.value = e.target.value;
      });
      this.bootSizeInput.addEventListener('change', () => {
        this.updateURLParams();
      });

      bootSelect.addEventListener('change', (e) => {
        this.bootSizeInput.value = e.target.value;
        this.bootSizeInput.dispatchEvent(new Event('input'));
        this.updateURLParams();
      });

      bootContainer.appendChild(bootSelect);
      this.bootSizeValue.parentNode.replaceChild(bootContainer, this.bootSizeValue);
    }
  }

  attachEventListeners() {
    this.riderLengthInput.addEventListener('input', (e) => {
      this.riderLengthValue.textContent = `${e.target.value} cm`;
    });

    this.bootSizeInput.addEventListener('input', (e) => {
      this.bootSizeValue.textContent = `EU ${e.target.value}`;
    });

    this.findBoardsButton.addEventListener('click', () => {
      this.findBoards();
    });

    if (this.sortBySelect) {
      this.sortBySelect.addEventListener('change', () => {
        if (this.lastFetchedProducts && Array.isArray(this.lastFetchedProducts)) {
          const sorted = this.sortBySelectedOption(this.lastFetchedProducts, this.sortBySelect.value);
          this.renderProducts(sorted);
        }
      });
    }

    window.addEventListener('popstate', (event) => {
      // Re-initialize state from URL and re-run the search
      this.initializeFromURL();
      const params = new URLSearchParams(window.location.search);
      if (params.has('riderLength') || params.has('bootSize')) {
        this.findBoards();
      } else {
        // Clear everything if no params
        this.productsGrid.innerHTML = '';
        this.productsList.innerHTML = '';
        this.selectedBoardStyles = [];
        this.initialVendors = [];
        this.vendorFilter?.classList.add('hidden');
        this.boardStyleFilter?.classList.add('hidden');
      }
    });
  }

  initializeSwiper() {
    if (!this.swiperContainer || !this.swiperWrapper) {
      return;
    }

    try {
      // Destroy existing swiper instance if it exists
      if (this.swiper) {
        this.swiper.destroy(true, true);
      }

      // Wait for DOM to be fully ready
      requestAnimationFrame(() => {
        this.swiper = new Swiper(this.swiperContainer, {
          wrapperClass: 'board-finder__swiper-wrapper',
          slideClass: 'swiper-slide',
          slidesPerView: 'auto',
          spaceBetween: 10,
          navigation: {
            nextEl: '.board-finder__swiper-button-next',
            prevEl: '.board-finder__swiper-button-prev',
          },
          breakpoints: {
            320: {
              slidesPerView: 2.6,
              spaceBetween: 10,
            },
            480: {
              slidesPerView: 2.6,
              spaceBetween: 10,
            },
            640: {
              slidesPerView: 3.6,
              spaceBetween: 10,
            },
            768: {
              slidesPerView: 6.6,
              spaceBetween: 10,
            },
          },
          on: {
            click: async (swiper, event) => {
              try {
                const clickedSlide = swiper.clickedSlide;
                if (!clickedSlide) return;

                const style = clickedSlide.getAttribute('data-style');
                if (!style) return;

                // Remove active class from all slides
                swiper.slides.forEach((slide) => {
                  slide.classList.remove('active');
                  const button = slide.querySelector('.board-style-slide');
                  if (button) {
                    button.setAttribute('aria-pressed', 'false');
                  }
                });

                if (this.selectedBoardStyles.includes(style)) {
                  // Deselect the style but keep swiper visible
                  this.selectedBoardStyles = [];
                } else {
                  // Select the style
                  clickedSlide.classList.add('active');
                  const button = clickedSlide.querySelector('.board-style-slide');
                  if (button) {
                    button.setAttribute('aria-pressed', 'true');
                  }
                  this.selectedBoardStyles = [style];
                }

                const riderLength = parseInt(this.riderLengthInput.value);
                const bootSize = parseFloat(this.bootSizeInput.value);
                let products = await this.fetchProducts(riderLength, bootSize, this.selectedBoardStyles);

                products = Array.isArray(products) ? products.filter((p) => p != null) : [];

                if (this.selectedBoardStyles.length === 1) {
                  const sortingMetafield = this.styleMetafieldMap[style];
                  if (sortingMetafield) {
                    try {
                      products = this.sortProductsByMetafield(products, sortingMetafield);
                    } catch (error) {
                      // Error handling without console
                    }
                  }
                }

                // Update vendor filter with filtered products
                this.updateVendorFilter(products);

                this.renderProducts(products);
                this.updateURLParams();
              } catch (error) {
                this.renderProducts([]);
              }
            },
          },
        });
      });
    } catch (error) {
      // Error handling without console
    }
  }

  updateBoardStylesSwiper(styles) {
    if (!this.swiperWrapper) {
      return;
    }

    try {
      // Clear existing slides
      this.swiperWrapper.innerHTML = '';

      if (styles.length > 0) {
        this.boardStyleFilter.classList.remove('hidden');

        // Style mapping for translations
        const styleTranslationMap = {
          Freestyle: 'freestyle',
          'All Mountain': 'all_mountain',
          Puder: 'puder',
          Carving: 'carving',
          Kids: 'kids',
        };

        // Add new slides
        styles.forEach((style) => {
          const slide = document.createElement('div');
          slide.className = 'swiper-slide';
          slide.setAttribute('data-style', style);

          if (this.selectedBoardStyles.includes(style)) {
            slide.classList.add('active');
          }

          // Get translation for the style
          const translationKey = styleTranslationMap[style];
          let displayText = style;
          if (translationKey && window.theme?.strings?.board_finder?.styles?.[translationKey]) {
            displayText = window.theme.strings.board_finder.styles[translationKey];
          }

          slide.innerHTML = `
            <div class="board-style-slide">
              <span>${displayText}</span>
            </div>
          `;
          this.swiperWrapper.appendChild(slide);
        });

        // Use requestAnimationFrame to ensure DOM is updated
        requestAnimationFrame(() => {
          this.initializeSwiper();
        });
      } else {
        this.boardStyleFilter.classList.add('hidden');
      }
    } catch (error) {
      // Error handling without console
    }
  }

  async findBoards() {
    // Only reset selections if it's a new search, not from a URL load
    if (!this.isInitialLoadWithParams) {
      // Reset board styles selection
      this.selectedBoardStyles = [];

      // Reset selected products and clear localStorage
      this.selectedProducts.clear();
      localStorage.removeItem('selectedBoardProducts');

      // Remove active class from all style slides if they exist
      if (this.swiper) {
        this.swiper.slides.forEach((slide) => {
          slide.classList.remove('active');
          const button = slide.querySelector('.board-style-slide');
          if (button) {
            button.setAttribute('aria-pressed', 'false');
          }
        });
      }
    }

    const riderLength = parseInt(this.riderLengthInput.value);
    const bootSize = parseFloat(this.bootSizeInput.value);

    try {
      this.showLoading();

      let products = await this.fetchProducts(riderLength, bootSize, []);
      products = Array.isArray(products) ? products.filter((p) => p != null) : [];

      this.lastFetchedProducts = [...products];

      // Show view switcher if we have products
      if (products.length > 0) {
        document.querySelector('.view-switcher')?.classList.remove('hidden');
      } else {
        document.querySelector('.view-switcher')?.classList.add('hidden');
      }

      // Extract unique board styles from filtered products
      const uniqueStyles = new Set();
      products.forEach((product) => {
        if (!product?.metafields) return;

        const boardStyleField = product.metafields.find((field) => field?.key === 'board_style');
        if (boardStyleField?.value) {
          try {
            const styles = JSON.parse(boardStyleField.value);
            if (Array.isArray(styles)) {
              styles.forEach((style) => uniqueStyles.add(style));
            }
          } catch (error) {
            // Error handling without console
          }
        }
      });

      // Update the board styles swiper
      this.updateBoardStylesSwiper(Array.from(uniqueStyles));

      // Update vendor filter
      this.updateVendorFilter(products);

      this.renderProducts(products);
      this.updateURLParams();
    } catch (error) {
      this.renderProducts([]);
      this.vendorFilter?.classList.add('hidden');
    } finally {
      this.hideLoading();
      // Reset the flag after the first run
      this.isInitialLoadWithParams = false;
    }
  }

  sortProductsByMetafield(products, metafieldKey) {
    // Early return if invalid inputs
    if (!Array.isArray(products)) {
      return [];
    }

    if (!metafieldKey) {
      return products;
    }

    // Filter out any null/undefined products first
    const validProducts = products.filter((product) => product != null);

    try {
      return validProducts.sort((a, b) => {
        // Safely get metafield values
        const aValue = this.getMetafieldValue(a, metafieldKey);
        const bValue = this.getMetafieldValue(b, metafieldKey);

        return bValue - aValue; // Sort descending
      });
    } catch (error) {
      return validProducts;
    }
  }

  // Helper method to safely get metafield values
  getMetafieldValue(product, key) {
    try {
      const metafield = product.metafields?.find((field) => field?.key === key);
      if (!metafield?.value) return '-';

      const value = parseInt(metafield.value);
      return isNaN(value) ? '-' : value;
    } catch (error) {
      return '-';
    }
  }

  showLoading() {
    if (this.loadingOverlay) {
      this.loadingOverlay.classList.remove('hidden');
    }
  }

  hideLoading() {
    if (this.loadingOverlay) {
      this.loadingOverlay.classList.add('hidden');
    }
  }

  async fetchProducts(riderLength, bootSize, boardStyles) {
    try {
      const response = await fetch('/api/2024-01/graphql.json', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Shopify-Storefront-Access-Token': getBoardFinderStorefrontAccessToken(),
        },
        body: JSON.stringify({
          query: `
            query GetProducts {
              collection(handle: "snowboards") {
                products(first: 250) {
                  nodes {
                    id
                    title
                    vendor
                    handle
                    tags
                    availableForSale
                    featuredImage {
                      url(transform: {maxWidth: 200, maxHeight: 200})
                      urlMedium: url(transform: {maxWidth: 400, maxHeight: 400})
                      urlLarge: url(transform: {maxWidth: 600, maxHeight: 600})
                      altText
                      width
                      height
                    }
                    priceRange {
                      minVariantPrice {
                        amount
                        currencyCode
                      }
                    }
                    compareAtPriceRange {
                      minVariantPrice {
                        amount
                        currencyCode
                      }
                    }
                    metafields(
                      identifiers: [
                        {namespace: "custom", key: "board_style"},
                        {namespace: "custom", key: "piste_carving_value"},
                        {namespace: "custom", key: "freestyle_park_value"},
                        {namespace: "custom", key: "powder_value"},
                        {namespace: "custom", key: "freeride_value"},
                        {namespace: "custom", key: "flex_feel_value"},
                        {namespace: "custom", key: "snowboard_shape"},
                        {namespace: "custom", key: "snowboard_profile"}
                      ]
                    ) {
                      key
                      value
                    }
                    variants(first: 100) {
                      edges {
                        node {
                          id
                          title
                          availableForSale
                          price {
                            amount
                            currencyCode
                          }
                          compareAtPrice {
                            amount
                            currencyCode
                          }
                          metafields(
                            identifiers: [
                              {namespace: "custom", key: "max_boot_size"},
                              {namespace: "custom", key: "rider_length_min"},
                              {namespace: "custom", key: "rider_length_max"},
                              {namespace: "custom", key: "boardwaistwidth"},
                              {namespace: "custom", key: "boardradius"}
                            ]
                          ) {
                            key
                            value
                          }
                        }
                      }
                    }
                  }
                }
              }
            }
          `,
        }),
      });

      const result = await response.json();
      if (result.errors) {
        return [];
      }

      const products =
        window.inlandetHiddenProducts?.filterProducts(result.data?.collection?.products?.nodes || []) ||
        result.data?.collection?.products?.nodes ||
        [];
      const filteredProducts = products.filter((product) =>
        this.checkProductMatch(product, product.variants.edges, riderLength, bootSize, boardStyles)
      );

      return filteredProducts;
    } catch (error) {
      return [];
    }
  }

  renderProducts(products) {
    this.updateViewUI(); // Set UI state right before rendering
    if (this.currentView === 'list') {
      this.renderListView(products);
      // Add a small delay to ensure DOM is updated
      requestAnimationFrame(() => {
        this.initializeMetricBars();
      });
    } else {
      this.renderGridView(products);
    }
  }

  renderGridView(products) {
    // Clear previous products
    this.productsGrid.innerHTML = '';

    if (!Array.isArray(products) || products.length === 0) {
      this.productsGrid.innerHTML = '<p>No boards found matching your size and style.</p>';
      return;
    }

    const riderLength = parseInt(this.riderLengthInput.value);
    const bootSize = parseFloat(this.bootSizeInput.value);

    this.productsGrid.innerHTML = products
      .map((product) => {
        const variants = product.variants?.edges || [];

        // Process variants with matching information
        const processedVariants = variants
          .filter(({ node: variant }) => variant.availableForSale)
          .map(({ node: variant }) => {
            const variantMetafields = variant.metafields.reduce((acc, field) => {
              if (field && field.key && field.value) {
                acc[field.key] = field.value;
              }
              return acc;
            }, {});

            const maxBootSize = parseFloat(variantMetafields.max_boot_size) || 0;
            const minLength = parseInt(variantMetafields.rider_length_min) || 0;
            const maxLength = parseInt(variantMetafields.rider_length_max) || 0;

            const isMatch = minLength <= riderLength && maxLength >= riderLength && maxBootSize >= bootSize;

            return {
              id: variant.id,
              title: variant.title,
              isMatch: isMatch,
              price: variant.price,
              compareAtPrice: variant.compareAtPrice,
            };
          })
          .sort((a, b) => {
            const aNum = parseInt(a.title.match(/\d+/)?.[0] || '0');
            const bNum = parseInt(b.title.match(/\d+/)?.[0] || '0');
            return aNum - bNum;
          });

        // Format all variants with bold for matches
        const formattedVariants = processedVariants.map((variant) =>
          variant.isMatch ? `<span style="color: #000;"><strong>${variant.title}</strong></span>` : variant.title
        );

        // Get the minimum price and compare-at price from all variants
        const minPrice = Math.min(...processedVariants.map((v) => parseFloat(v.price?.amount || 0)));
        const minCompareAtPrice = Math.min(...processedVariants.map((v) => parseFloat(v.compareAtPrice?.amount || 0)));

        // Determine if the product is on sale
        const isOnSale = minCompareAtPrice > minPrice;

        // Generate price HTML
        const priceHtml = isOnSale
          ? `
        <div class="price price--on-sale">
          <div class="price__container">
            <div class="price__sale">
              <span class="visually-hidden visually-hidden--inline">Regular price</span>
              <span>
                <s class="price-item price-item--regular">
                  ${this.formatMoney(minCompareAtPrice)}
                </s>
              </span>
              <span class="visually-hidden visually-hidden--inline">Sale price</span>
              <span class="price-item price-item--sale price-item--last">
                ${this.formatMoney(minPrice)}
              </span>
            </div>
          </div>
        </div>
      `
          : `
        <div class="price">
          <div class="price__container">
            <div class="price__regular">
              <span class="visually-hidden visually-hidden--inline">Regular price</span>
              <span class="price-item price-item--regular">
                ${this.formatMoney(minPrice)}
              </span>
            </div>
          </div>
        </div>
      `;

        return `
        <div class="product-item">
          <a href="/products/${product.handle}" class="product-item__link">
            <div class="product-item__image-wrapper">
              ${
                product.featuredImage
                  ? `
                <img
                  src="${product.featuredImage.url.replace('_200x200', '_400x400')}"
                  alt="${product.featuredImage.altText || product.title}"
                  loading="lazy"
                  class="product-item__image"
                  onerror="this.src='https://cdn.shopify.com/s/files/1/0867/3823/9832/files/fallback-image.png?v=1737318396'"
                >
              `
                  : `
                <div class="product-item__no-image-placeholder">
                  <span class="placeholder-text">${product.title}</span>
                </div>
              `
              }
              <div class="product-item__compare">
                <sc-add-to-compare product-handle="${product.handle}">
                  <label class="compare-label">
                    <input type="checkbox" class="compare-checkbox" aria-label="Compare ${product.title}">
                    <span class="visually-hidden">Compare</span>
                  </label>
                </sc-add-to-compare>
              </div>
            </div>
            
            <div class="product-item__info">
              ${
                product.vendor
                  ? `
                <p class="product-item__vendor">${product.vendor}</p>
              `
                  : ''
              }
              
              <h3 class="product-item__title">
                ${product.title}
              </h3>
              
              ${priceHtml}
              
              <div class="product-item__variants">
                ${formattedVariants.join(' • ')}
              </div>
            </div>
          </a>
        </div>
      `;
      })
      .join('');

    this.updateURLParams();
  }

  renderListView(products) {
    // Create flat array of variants with all necessary data
    this.listViewVariants = products.flatMap((product) => {
      return product.variants.edges
        .filter(({ node: variant }) => {
          // Only include variants that are in stock and match the criteria
          if (!variant.availableForSale) return false;

          // If we're loading all boards, don't filter by rider criteria
          if (this.isLoadingAll) return true;

          const riderLength = parseFloat(this.riderLengthInput?.value || 0);
          const bootSize = parseFloat(this.bootSizeInput?.value || 0);

          // Get variant metafields
          const variantMetafields = variant.metafields.reduce((acc, field) => {
            if (field && field.key && field.value) {
              acc[field.key] = field.value;
            }
            return acc;
          }, {});

          // Parse the values, defaulting to 0 if invalid
          const maxBootSize = parseFloat(variantMetafields.max_boot_size) || 0;
          const minLength = parseInt(variantMetafields.rider_length_min) || 0;
          const maxLength = parseInt(variantMetafields.rider_length_max) || 0;

          // Check if variant matches the criteria
          return minLength <= riderLength && maxLength >= riderLength && maxBootSize >= bootSize;
        })
        .map(({ node: variant }) => ({
          id: variant.id,
          variant,
          product,
          flexValue: this.getMetafieldValue(product, 'flex_feel_value'),
          carvingValue: this.getMetafieldValue(product, 'piste_carving_value'),
          freerideValue: this.getMetafieldValue(product, 'freeride_value'),
          powderValue: this.getMetafieldValue(product, 'powder_value'),
          freestyleValue: this.getMetafieldValue(product, 'freestyle_park_value'),
          shapeValue: this.getJsonMetafieldValue(product, 'snowboard_shape'),
          profileValue: this.getJsonMetafieldValue(product, 'snowboard_profile'),
          waistWidth: this.getVariantMetafieldValue(variant, 'boardwaistwidth'),
          radius: this.getVariantMetafieldValue(variant, 'boardradius'),
          maxBoot: this.getVariantMetafieldValue(variant, 'max_boot_size'),
          minLength: this.getVariantMetafieldValue(variant, 'rider_length_min'),
          maxLength: this.getVariantMetafieldValue(variant, 'rider_length_max'),
        }));
    });

    // Split into selected and unselected variants
    const selectedVariants = this.listViewVariants.filter((v) => this.selectedProducts.has(v.id));
    const unselectedVariants = this.listViewVariants.filter((v) => !this.selectedProducts.has(v.id));

    this.productsList.innerHTML = `
      <div class="products-table-container">
        <div class="products-table-wrapper">
          <table class="products-table">
            <thead>
              <tr>
                <th class="sticky-left checkbox-column">Sort</th>
                <th class="sticky-left">Image</th>
                <th class="sortable" data-sort="name">Name</th>
                <th class="sortable" data-sort="vendor">Brand</th>
                <th class="sortable" data-sort="price">Price</th>
                <th class="sortable" data-sort="length">Length</th>
                <th class="sortable" data-sort="width">Width</th>
                <th class="sortable" data-sort="radius">Radius</th>
                <th class="sortable" data-sort="bootsize">Max Boot</th>
                <th class="sortable" data-sort="minlength">Min Length</th>
                <th class="sortable" data-sort="maxlength">Max Length</th>
                <th class="sortable" data-sort="flex">Flex</th>
                <th class="sortable" data-sort="carving">Carving</th>
                <th class="sortable" data-sort="freeride">Freeride</th>
                <th class="sortable" data-sort="powder">Powder</th>
                <th class="sortable" data-sort="freestyle">Freestyle</th>
                <th class="sortable" data-sort="shape">Shape</th>
                <th class="sortable" data-sort="profile">Profile</th>
              </tr>
            </thead>
            <tbody>
              ${this.renderVariantRows(selectedVariants, true)}
              ${
                selectedVariants.length > 0 && unselectedVariants.length > 0
                  ? `
                <tr class="divider-row">
                  <td colspan="16"></td>
                </tr>
              `
                  : ''
              }
              ${this.renderVariantRows(unselectedVariants, false)}
            </tbody>
          </table>
        </div>
      </div>
    `;

    this.addCheckboxListeners();
    this.addSortListeners();

    // Apply any existing sort
    if (this.currentSortColumn) {
      const header = this.productsList.querySelector(`th[data-sort="${this.currentSortColumn}"]`);
      if (header) {
        header.classList.add(this.currentSortDirection ? 'sort-asc' : 'sort-desc');
      }
      this.applySorting();
    }

    // Add this to your renderListView method after rendering the table
    const tableWrapper = this.productsList.querySelector('.products-table-wrapper');
    if (tableWrapper) {
      tableWrapper.addEventListener('scroll', () => {
        if (tableWrapper.scrollLeft > 0) {
          tableWrapper.classList.add('is-scrolled');
        } else {
          tableWrapper.classList.remove('is-scrolled');
        }
      });
    }

    this.initTableDrag();
  }

  renderVariantRows(variants, isSelected) {
    return variants
      .map((variant) => {
        // Helper function to create bar HTML
        const createBarHtml = (value) => {
          if (value === '-' || !value) return '-';
          const numericValue = parseInt(value);
          if (isNaN(numericValue)) return '-';

          // Create a unique class based on the value range
          const getBarClass = (value) => {
            if (value < 21) return 'bar-very-low';
            if (value < 31) return 'bar-low';
            if (value < 61) return 'bar-medium';
            if (value < 81) return 'bar-high';
            return 'bar-very-high';
          };

          return `
          <div class="metric-container">
            <div class="bar">
              <div class="bar-fill ${getBarClass(numericValue)}" 
                   data-value="${numericValue}" 
                   style="width: ${numericValue}%">
              </div>
            </div>
            <div class="bar-value">${numericValue}</div>
          </div>
        `;
        };

        // Generate price HTML for the variant
        const priceHtml =
          variant.variant.compareAtPrice?.amount &&
          parseFloat(variant.variant.compareAtPrice.amount) > parseFloat(variant.variant.price.amount)
            ? `
        <div class="price price--on-sale">
          <div class="price__container">
            <div class="price__sale">
              <span class="visually-hidden visually-hidden--inline">Regular price</span>
              <span>
                <s class="price-item price-item--regular">
                  ${this.formatMoney(variant.variant.compareAtPrice.amount)}
                </s>
              </span>
              <span class="visually-hidden visually-hidden--inline">Sale price</span>
              <span class="price-item price-item--sale price-item--last">
                ${this.formatMoney(variant.variant.price.amount)}
              </span>
            </div>
          </div>
        </div>
      `
            : `
        <div class="price">
          <div class="price__container">
            <div class="price__regular">
              <span class="visually-hidden visually-hidden--inline">Regular price</span>
              <span class="price-item price-item--regular">
                ${this.formatMoney(variant.variant.price.amount)}
              </span>
            </div>
          </div>
        </div>
      `;

        return `
        <tr class="${isSelected ? 'selected-row' : ''}">
          <td class="sticky-left checkbox-column">
            <label class="checkbox-wrapper">
              <input 
                type="checkbox" 
                class="product-selector" 
                data-product-id="${variant.id}"
                ${isSelected ? 'checked' : ''}
                aria-label="Select ${variant.product.title} - ${variant.variant.title}"
              >
              <span class="custom-checkbox"></span>
            </label>
          </td>
          <td class="image-column">
            <div class="product-table-image-wrapper">
              <img 
                src="${variant.product.featuredImage?.url.replace('_200x200', '_400x400') || ''}" 
                alt="${variant.product.featuredImage?.altText || variant.product.title || 'Product image'}"
                class="product-table-image"
                width="80"
                height="80"
                loading="lazy"
                onerror="this.src='https://cdn.shopify.com/s/files/1/0867/3823/9832/files/fallback-image.png?v=1737318396'"
              >
              <div class="product-item__compare">
                <sc-add-to-compare product-handle="${variant.product.handle || ''}">
                  <label class="compare-label">
                    <input type="checkbox" class="compare-checkbox" aria-label="Compare ${variant.product.title}">
                    <span class="visually-hidden">Compare</span>
                  </label>
                </sc-add-to-compare>
              </div>
            </div>
          </td>
          <td class="name-column">
            <a href="/products/${variant.product.handle || ''}" class="product-link">
              ${variant.product.title || 'Unknown'} - ${variant.variant.title || ''}
            </a>
          </td>
          <td class="vendor-column">${variant.product.vendor || '-'}</td>
          <td data-price="${variant.variant.price.amount || 0}">
            ${priceHtml}
          </td>
          <td data-length="${variant.variant.title || ''}">${variant.variant.title || '-'}</td>
          <td data-width="${variant.waistWidth || 0}">${variant.waistWidth ? `${variant.waistWidth}mm` : '-'}</td>
          <td data-radius="${variant.radius || 0}">${variant.radius ? `${variant.radius}m` : '-'}</td>
          <td data-bootsize="${variant.maxBoot || 0}">${variant.maxBoot ? `EU ${variant.maxBoot}` : '-'}</td>
          <td data-minlength="${variant.minLength || 0}">${variant.minLength ? `${variant.minLength}cm` : '-'}</td>
          <td data-maxlength="${variant.maxLength || 0}">${variant.maxLength ? `${variant.maxLength}cm` : '-'}</td>
          <td data-flex="${variant.flexValue || 0}">
            ${createBarHtml(variant.flexValue)}
          </td>
          <td data-carving="${variant.carvingValue || 0}">
            ${createBarHtml(variant.carvingValue)}
          </td>
          <td data-freeride="${variant.freerideValue || 0}">
            ${createBarHtml(variant.freerideValue)}
          </td>
          <td data-powder="${variant.powderValue || 0}">
            ${createBarHtml(variant.powderValue)}
          </td>
          <td data-freestyle="${variant.freestyleValue || 0}">
            ${createBarHtml(variant.freestyleValue)}
          </td>
          <td data-shape="${variant.shapeValue || ''}">${variant.shapeValue || '-'}</td>
          <td data-profile="${variant.profileValue || ''}">${variant.profileValue || '-'}</td>
        </tr>
      `;
      })
      .join('');
  }

  addCheckboxListeners() {
    const checkboxes = this.productsList.querySelectorAll('.product-selector');
    checkboxes.forEach((checkbox) => {
      checkbox.addEventListener('change', async (event) => {
        const variantId = event.target.dataset.productId;
        const row = event.target.closest('tr');

        try {
          if (event.target.checked) {
            row.classList.add('selecting-row');
            await new Promise((resolve) => setTimeout(resolve, 500));
            this.selectedProducts.add(variantId);
          } else {
            this.selectedProducts.delete(variantId);
          }

          localStorage.setItem('selectedBoardProducts', JSON.stringify([...this.selectedProducts]));

          // Instead of re-rendering everything, just apply current sort
          this.applySorting();
        } catch (error) {
          console.error('Error saving selection:', error);
        }
      });
    });
  }

  formatMoney(amount) {
    // Convert amount to number and round to nearest integer (removes decimals)
    const roundedAmount = Math.round(parseFloat(amount));

    // Format with Swedish locale but without currency symbol
    const formattedNumber = new Intl.NumberFormat('sv-SE', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(roundedAmount);

    // Return formatted string with SEK
    return `${formattedNumber} SEK`;
  }

  fetchRanges() {
    console.log('Fetching ranges');
  }

  checkProductMatch(product, variants, riderLength, bootSize, boardStyles) {
    if (!product || !variants) return false;

    const hasMatchingVariant = variants.some(({ node: variant }) => {
      if (!variant || !variant.availableForSale || !variant.metafields) return false;

      const variantMetafields = variant.metafields.reduce((acc, field) => {
        if (field && field.key && field.value) {
          acc[field.key] = field.value;
        }
        return acc;
      }, {});

      const maxBootSize = parseFloat(variantMetafields.max_boot_size) || 0;
      const minLength = parseInt(variantMetafields.rider_length_min) || 0;
      const maxLength = parseInt(variantMetafields.rider_length_max) || 0;

      return minLength <= riderLength && maxLength >= riderLength && maxBootSize >= bootSize;
    });

    if (!hasMatchingVariant) return false;

    if (boardStyles && boardStyles.length > 0) {
      const boardStyleField = product.metafields.find((field) => field && field.key === 'board_style');

      if (!boardStyleField || !boardStyleField.value) {
        return false;
      }

      try {
        const productStyles = JSON.parse(boardStyleField.value);
        const hasMatchingStyle = boardStyles.some(
          (style) => productStyles && Array.isArray(productStyles) && productStyles.includes(style)
        );
        return hasMatchingStyle;
      } catch (error) {
        console.error('Error parsing board styles for product:', product.title, error);
        return false;
      }
    }

    return true;
  }

  // Add new sorting methods
  sortBySelectedOption(products, selectedValue) {
    if (!Array.isArray(products)) {
      console.error('Products is not an array:', products);
      return [];
    }

    let productsToSort = [...products];
    if (this.selectedBoardStyles && this.selectedBoardStyles.length > 0) {
      productsToSort = productsToSort.filter((product) => {
        const boardStyleField = product.metafields.find((field) => field?.key === 'board_style');
        if (!boardStyleField?.value) return false;

        try {
          const productStyles = JSON.parse(boardStyleField.value);
          return productStyles.includes(this.selectedBoardStyles[0]);
        } catch (error) {
          console.error('Error parsing board styles for product:', product.title, error);
          return false;
        }
      });
    }

    switch (selectedValue) {
      case 'waistwidthdesc':
        productsToSort = this.sortByWaistWidthDesc(productsToSort);
        break;
      case 'flexdesc':
        productsToSort = this.sortByFlexDesc(productsToSort);
        break;
    }

    return productsToSort;
  }

  sortByWaistWidthDesc(products) {
    return [...products].sort((a, b) => {
      const aMax = this.getMaxWaistWidth(a);
      const bMax = this.getMaxWaistWidth(b);
      return bMax - aMax;
    });
  }

  getMaxWaistWidth(product) {
    try {
      const riderLength = parseInt(this.riderLengthInput.value);
      const bootSize = parseFloat(this.bootSizeInput.value);

      let maxWidth = 0;

      if (!product?.variants?.edges || !Array.isArray(product.variants.edges)) {
        console.log('No valid variants for product:', product?.title);
        return 0;
      }

      for (const edge of product.variants.edges) {
        const variant = edge?.node;
        if (!variant?.metafields || !Array.isArray(variant.metafields)) continue;

        // Check if variant matches user criteria
        const variantMetafields = variant.metafields.reduce((acc, field) => {
          if (field && typeof field === 'object' && field.key && field.value) {
            acc[field.key] = field.value;
          }
          return acc;
        }, {});

        const maxBootSize = parseFloat(variantMetafields.max_boot_size) || 0;
        const minLength = parseInt(variantMetafields.rider_length_min) || 0;
        const maxLength = parseInt(variantMetafields.rider_length_max) || 0;

        if (minLength <= riderLength && maxLength >= riderLength && maxBootSize >= bootSize) {
          const waistField = variant.metafields.find((f) => f && typeof f === 'object' && f.key === 'boardwaistwidth');

          if (waistField?.value) {
            const val = parseInt(waistField.value, 10);
            if (!isNaN(val) && val > maxWidth) maxWidth = val;
          }
        }
      }
      return maxWidth;
    } catch (error) {
      console.error('Error calculating waist width for product:', product?.title, error);
      return 0;
    }
  }

  sortByFlexDesc(products) {
    return [...products].sort((a, b) => {
      const aFlex = this.getMaxFlex(a);
      const bFlex = this.getMaxFlex(b);
      return bFlex - aFlex;
    });
  }

  getMaxFlex(product) {
    try {
      // Check if product and metafields exist
      if (!product?.metafields || !Array.isArray(product.metafields)) {
        console.log('No valid metafields for product:', product?.title);
        return 0;
      }

      // Find flex_feel_value metafield
      const flexField = product.metafields.find(
        (field) => field && typeof field === 'object' && field.key === 'flex_feel_value'
      );

      // If we found a flex field, log which one we found
      if (flexField) {
        console.log('Found flex field for', product.title, ':', flexField.key, flexField.value);
      }

      // Check if flexField and its value exist
      if (!flexField?.value) {
        console.log('No flex value found for product:', product.title);
        return 0;
      }

      // Parse the integer value directly
      const flexValue = parseInt(flexField.value, 10);
      return isNaN(flexValue) ? 0 : flexValue;
    } catch (error) {
      console.error('Error calculating flex for product:', product?.title, error);
      return 0;
    }
  }

  updateVendorFilter(products) {
    if (!this.vendorFilter || !this.vendorWrapper) {
      return;
    }

    try {
      // If a board style is selected, filter products first
      let filteredProducts = [...products];
      if (this.selectedBoardStyles && this.selectedBoardStyles.length > 0) {
        filteredProducts = filteredProducts.filter((product) => {
          const boardStyleField = product.metafields.find((field) => field?.key === 'board_style');
          if (!boardStyleField?.value) return false;

          try {
            const productStyles = JSON.parse(boardStyleField.value);
            return productStyles.includes(this.selectedBoardStyles[0]);
          } catch (error) {
            return false;
          }
        });
      }

      // Extract unique vendors from filtered products
      const uniqueVendors = [...new Set(filteredProducts.map((product) => product.vendor))].filter(Boolean);

      // Clear existing vendor buttons
      this.vendorWrapper.innerHTML = '';

      if (uniqueVendors.length > 0) {
        this.vendorFilter.classList.remove('hidden');

        // Add vendor buttons
        uniqueVendors.forEach((vendor) => {
          const button = document.createElement('button');
          button.className = 'vendor-button';
          button.setAttribute('data-vendor', vendor);
          button.innerHTML = `<span>${vendor}</span>`;

          if (this.initialVendors.includes(vendor)) {
            button.classList.add('active');
          }

          // Add click handler
          button.addEventListener('click', () => {
            // Toggle active state
            button.classList.toggle('active');

            // Get all selected vendors
            const selectedVendors = Array.from(this.vendorWrapper.querySelectorAll('.vendor-button.active')).map(
              (btn) => btn.getAttribute('data-vendor')
            );

            // Start with last fetched products
            let productsToShow = [...this.lastFetchedProducts];

            // Apply board style filter if selected
            if (this.selectedBoardStyles && this.selectedBoardStyles.length > 0) {
              productsToShow = productsToShow.filter((product) => {
                const boardStyleField = product.metafields.find((field) => field?.key === 'board_style');
                if (!boardStyleField?.value) return false;

                try {
                  const productStyles = JSON.parse(boardStyleField.value);
                  return productStyles.includes(this.selectedBoardStyles[0]);
                } catch (error) {
                  return false;
                }
              });
            }

            // Then apply vendor filter if any vendors are selected
            if (selectedVendors.length > 0) {
              productsToShow = productsToShow.filter((product) => selectedVendors.includes(product.vendor));
            }

            this.renderProducts(productsToShow);
            this.updateURLParams();
          });

          this.vendorWrapper.appendChild(button);
        });
      } else {
        this.vendorFilter.classList.add('hidden');
      }
    } catch (error) {
      // Error handling without console
    }

    this.initialVendors = []; // Clear after use
  }

  initializeViewSwitcher() {
    this.viewButtons.forEach((button) => {
      button.addEventListener('click', () => {
        const view = button.dataset.view;
        this.switchView(view);
      });
    });

    // Create and add the load all button
    const viewSwitcher = document.querySelector('.view-switcher');
    if (viewSwitcher) {
      const loadAllButton = document.createElement('button');
      loadAllButton.className = 'load-all-button';
      loadAllButton.textContent = 'Load All Boards';
      loadAllButton.style.display = 'none'; // Initially hidden
      loadAllButton.addEventListener('click', () => this.loadAllBoards());
      viewSwitcher.appendChild(loadAllButton);
    }
  }

  async loadAllBoards() {
    try {
      this.showLoading();
      this.isLoadingAll = true; // Set the flag

      // Fetch all products
      const response = await fetch('/api/2024-01/graphql.json', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Shopify-Storefront-Access-Token': getBoardFinderStorefrontAccessToken(),
        },
        body: JSON.stringify({
          query: `
            query GetProducts {
              collection(handle: "snowboards") {
                products(first: 250) {
                  nodes {
                    id
                    title
                    vendor
                    handle
                    tags
                    availableForSale
                    featuredImage {
                      url(transform: {maxWidth: 200, maxHeight: 200})
                      urlMedium: url(transform: {maxWidth: 400, maxHeight: 400})
                      urlLarge: url(transform: {maxWidth: 600, maxHeight: 600})
                      altText
                      width
                      height
                    }
                    priceRange {
                      minVariantPrice {
                        amount
                        currencyCode
                      }
                    }
                    compareAtPriceRange {
                      minVariantPrice {
                        amount
                        currencyCode
                      }
                    }
                    metafields(
                      identifiers: [
                        {namespace: "custom", key: "board_style"},
                        {namespace: "custom", key: "piste_carving_value"},
                        {namespace: "custom", key: "freestyle_park_value"},
                        {namespace: "custom", key: "powder_value"},
                        {namespace: "custom", key: "freeride_value"},
                        {namespace: "custom", key: "flex_feel_value"},
                        {namespace: "custom", key: "snowboard_shape"},
                        {namespace: "custom", key: "snowboard_profile"}
                      ]
                    ) {
                      key
                      value
                    }
                    variants(first: 100) {
                      edges {
                        node {
                          id
                          title
                          availableForSale
                          price {
                            amount
                            currencyCode
                          }
                          compareAtPrice {
                            amount
                            currencyCode
                          }
                          metafields(
                            identifiers: [
                              {namespace: "custom", key: "max_boot_size"},
                              {namespace: "custom", key: "rider_length_min"},
                              {namespace: "custom", key: "rider_length_max"},
                              {namespace: "custom", key: "boardwaistwidth"},
                              {namespace: "custom", key: "boardradius"}
                            ]
                          ) {
                            key
                            value
                          }
                        }
                      }
                    }
                  }
                }
              }
            }
          `,
        }),
      });

      const result = await response.json();
      if (result.errors) {
        console.error('GraphQL Errors:', result.errors);
        return;
      }

      const products =
        window.inlandetHiddenProducts?.filterProducts(result.data?.collection?.products?.nodes || []) ||
        result.data?.collection?.products?.nodes ||
        [];

      // Only filter by availability
      const filteredProducts = products.filter(
        (product) => product.availableForSale && product.variants.edges.some(({ node }) => node.availableForSale)
      );

      this.lastFetchedProducts = [...filteredProducts];

      // Update vendor filter with all products
      this.updateVendorFilter(filteredProducts);

      // Render the products in list view
      this.renderProducts(filteredProducts);
      this.updateURLParams();
    } catch (error) {
      console.error('Error loading all boards:', error);
    } finally {
      this.hideLoading();
      this.isLoadingAll = false; // Reset the flag
    }
  }

  switchView(view) {
    this.currentView = view;
    this.updateViewUI();

    // Get the currently filtered/displayed products
    let productsToShow = this.lastFetchedProducts;

    // Apply current board style filter if any is selected
    if (this.selectedBoardStyles && this.selectedBoardStyles.length > 0) {
      productsToShow = productsToShow.filter((product) => {
        const boardStyleField = product.metafields.find((field) => field?.key === 'board_style');
        if (!boardStyleField?.value) return false;

        try {
          const productStyles = JSON.parse(boardStyleField.value);
          return productStyles.includes(this.selectedBoardStyles[0]);
        } catch (error) {
          console.error('Error parsing board styles for product:', product.title, error);
          return false;
        }
      });
    }

    // Get selected vendors from active vendor buttons
    const selectedVendors = Array.from(this.vendorWrapper.querySelectorAll('.vendor-button.active')).map((btn) =>
      btn.getAttribute('data-vendor')
    );

    // Apply vendor filter if any vendors are selected
    if (selectedVendors.length > 0) {
      productsToShow = productsToShow.filter((product) => selectedVendors.includes(product.vendor));
    }

    // Render the filtered products in the new view
    this.renderProducts(productsToShow);
    this.updateURLParams();
  }

  updateViewUI() {
    this.viewButtons.forEach(button => {
        button.classList.toggle('active', button.dataset.view === this.currentView);
    });
    this.productsGrid.classList.toggle('hidden', this.currentView === 'list');
    this.productsList.classList.toggle('hidden', this.currentView === 'grid');

    const loadAllButton = document.querySelector('.load-all-button');
    if (loadAllButton) {
      loadAllButton.style.display = this.currentView === 'list' ? 'block' : 'none';
    }
  }

  getVariantWaistWidth(variant) {
    try {
      if (!variant?.metafields) {
        return 0;
      }

      const waistWidthField = variant.metafields.find((f) => f?.key === 'boardwaistwidth');
      if (!waistWidthField) {
        return 0;
      }

      // Parse as float instead of integer to keep decimal places
      const value = parseFloat(waistWidthField.value);
      return isNaN(value) ? 0 : value;
    } catch (error) {
      return 0;
    }
  }

  getVariantLength(variant) {
    try {
      const lengthField = variant.title.match(/\d+/);
      return lengthField ? lengthField[0] : '---';
    } catch (error) {
      console.error('Error getting length:', error);
      return '---';
    }
  }

  isVariantMatchingCriteria(variant) {
    try {
      // First check if variant is in stock
      if (!variant.availableForSale) {
        return false;
      }

      const riderLength = parseInt(this.riderLengthInput.value);
      const bootSize = parseFloat(this.bootSizeInput.value);

      const variantMetafields = variant.metafields.reduce((acc, field) => {
        if (field && field.key && field.value) {
          acc[field.key] = field.value;
        }
        return acc;
      }, {});

      const maxBootSize = parseFloat(variantMetafields.max_boot_size) || 0;
      const minLength = parseInt(variantMetafields.rider_length_min) || 0;
      const maxLength = parseInt(variantMetafields.rider_length_max) || 0;

      return minLength <= riderLength && maxLength >= riderLength && maxBootSize >= bootSize;
    } catch (error) {
      console.error('Error checking variant criteria:', error);
      return false;
    }
  }

  // Add helper method to get variant metafield values
  getVariantMetafieldValue(variant, key) {
    try {
      const metafield = variant?.metafields?.find((field) => field?.key === key);
      if (!metafield?.value) return null;

      const value = parseFloat(metafield.value);
      return isNaN(value) ? null : value;
    } catch (error) {
      console.warn(`Error getting metafield value for key ${key}:`, error);
      return null;
    }
  }

  addSortListeners() {
    const headers = this.productsList.querySelectorAll('th.sortable');
    headers.forEach((header) => {
      header.addEventListener('click', () => {
        const sortKey = header.dataset.sort;

        // If header already has a sort class, toggle it
        // Otherwise use the default direction for this column
        const isAscending = header.classList.contains('sort-desc')
          ? true
          : header.classList.contains('sort-asc')
          ? false
          : this.defaultSortDirections[sortKey];

        // Store current sort state
        this.currentSortColumn = sortKey;
        this.currentSortDirection = isAscending;

        // Remove sort classes from all headers
        headers.forEach((h) => {
          h.classList.remove('sort-asc', 'sort-desc');
        });

        // Add sort class to clicked header
        header.classList.add(isAscending ? 'sort-asc' : 'sort-desc');

        this.applySorting();
      });
    });
  }

  applySorting() {
    if (!this.currentSortColumn) return;

    // Get all variants from the current view
    const allVariants = this.getAllCurrentVariants();

    // Split and sort each group independently
    const selectedVariants = this.sortVariants(
      allVariants.filter((v) => this.selectedProducts.has(v.id)),
      this.currentSortColumn,
      this.currentSortDirection
    );
    const unselectedVariants = this.sortVariants(
      allVariants.filter((v) => !this.selectedProducts.has(v.id)),
      this.currentSortColumn,
      this.currentSortDirection
    );

    // Re-render the table body
    const tbody = this.productsList.querySelector('tbody');
    tbody.innerHTML = `
      ${this.renderVariantRows(selectedVariants, true)}
      ${
        selectedVariants.length > 0 && unselectedVariants.length > 0
          ? `
        <tr class="divider-row">
          <td colspan="16"></td>
        </tr>
      `
          : ''
      }
      ${this.renderVariantRows(unselectedVariants, false)}
    `;

    // Reattach checkbox listeners
    this.addCheckboxListeners();

    // Reinitialize metric bars after sorting
    requestAnimationFrame(() => {
      this.initializeMetricBars();
    });
  }

  sortVariants(variants, sortKey, isAscending) {
    const getSortValue = (variant) => {
      let value;
      switch (sortKey) {
        case 'name':
          value = variant.product.title || '';
          return { value, isEmpty: !value };
        case 'vendor':
          value = variant.product.vendor || '';
          return { value, isEmpty: !value };
        case 'price':
          value = parseFloat(variant.product.priceRange?.minVariantPrice?.amount || 0);
          return { value, isEmpty: value === 0 };
        case 'length':
          value = parseInt(variant.variant.title?.match(/\d+/)?.[0] || 0);
          return { value, isEmpty: value === 0 };
        case 'width':
          value = parseFloat(variant.waistWidth || 0);
          return { value, isEmpty: !variant.waistWidth };
        case 'radius':
          value = parseFloat(variant.radius || 0);
          return { value, isEmpty: !variant.radius };
        case 'bootsize':
          value = parseFloat(variant.maxBoot || 0);
          return { value, isEmpty: !variant.maxBoot };
        case 'minlength':
          value = parseInt(variant.minLength || 0);
          return { value, isEmpty: !variant.minLength };
        case 'maxlength':
          value = parseInt(variant.maxLength || 0);
          return { value, isEmpty: !variant.maxLength };
        case 'flex':
          value = parseInt(variant.flexValue || 0);
          return { value, isEmpty: variant.flexValue === '-' || !variant.flexValue };
        case 'carving':
          value = parseInt(variant.carvingValue || 0);
          return { value, isEmpty: variant.carvingValue === '-' || !variant.carvingValue };
        case 'freeride':
          value = parseInt(variant.freerideValue || 0);
          return { value, isEmpty: variant.freerideValue === '-' || !variant.freerideValue };
        case 'powder':
          value = parseInt(variant.powderValue || 0);
          return { value, isEmpty: variant.powderValue === '-' || !variant.powderValue };
        case 'freestyle':
          value = parseInt(variant.freestyleValue || 0);
          return { value, isEmpty: variant.freestyleValue === '-' || !variant.freestyleValue };
        case 'shape':
          value = variant.shapeValue || '';
          return { value, isEmpty: !value || value === '-' };
        case 'profile':
          value = variant.profileValue || '';
          return { value, isEmpty: !value || value === '-' };
        default:
          return { value: 0, isEmpty: true };
      }
    };

    return [...variants].sort((a, b) => {
      const aData = getSortValue(a);
      const bData = getSortValue(b);

      // If one is empty and the other isn't, empty goes last
      if (aData.isEmpty !== bData.isEmpty) {
        return aData.isEmpty ? 1 : -1;
      }

      // If both are empty, maintain original order
      if (aData.isEmpty && bData.isEmpty) {
        return 0;
      }

      // Normal sort for non-empty values
      if (typeof aData.value === 'string') {
        return isAscending ? aData.value.localeCompare(bData.value) : bData.value.localeCompare(aData.value);
      }

      return isAscending ? aData.value - bData.value : bData.value - aData.value;
    });
  }

  getAllCurrentVariants() {
    // Always filter by availability
    let filteredVariants = this.listViewVariants;

    // If we're not explicitly loading all boards, apply the rider filters
    if (!this.isLoadingAll) {
      const riderLength = parseFloat(this.riderLengthInput?.value || 0);
      const bootSize = parseFloat(this.bootSizeInput?.value || 0);

      filteredVariants = filteredVariants.filter((variant) => {
        // Parse min and max lengths, defaulting to 0 if invalid
        const minLength = parseFloat(variant.minLength) || 0;
        const maxLength = parseFloat(variant.maxLength) || 0;
        const maxBoot = parseFloat(variant.maxBoot) || 0;

        // Check if the variant matches all criteria
        return minLength <= riderLength && maxLength >= riderLength && maxBoot >= bootSize;
      });
    }

    return filteredVariants;
  }

  // Optional: Add method to apply initial sort
  applyInitialSort() {
    // You could choose a default column to sort by when the table first loads
    const defaultSortColumn = 'vendor'; // or any other column
    const header = this.productsList.querySelector(`th.sortable[data-sort="${defaultSortColumn}"]`);
    if (header) {
      header.classList.add(this.defaultSortDirections[defaultSortColumn] ? 'sort-asc' : 'sort-desc');
      const allVariants = this.getAllCurrentVariants();
      const selectedVariants = this.sortVariants(
        allVariants.filter((v) => this.selectedProducts.has(v.id)),
        defaultSortColumn,
        this.defaultSortDirections[defaultSortColumn]
      );
      const unselectedVariants = this.sortVariants(
        allVariants.filter((v) => !this.selectedProducts.has(v.id)),
        defaultSortColumn,
        this.defaultSortDirections[defaultSortColumn]
      );

      const tbody = this.productsList.querySelector('tbody');
      tbody.innerHTML = `
        ${this.renderVariantRows(selectedVariants, true)}
        ${
          selectedVariants.length > 0 && unselectedVariants.length > 0
            ? `
          <tr class="divider-row">
            <td colspan="16"></td>
          </tr>
        `
            : ''
        }
        ${this.renderVariantRows(unselectedVariants, false)}
      `;

      this.addCheckboxListeners();
    }
  }

  initTableDrag() {
    const container = this.productsList.querySelector('.products-table-container');
    let isDown = false;
    let startX;
    let scrollLeft;

    container.addEventListener('mousedown', (e) => {
      // Ignore if clicking on interactive elements
      if (e.target.closest('a, input, label')) return;

      isDown = true;
      container.classList.add('grabbing');
      startX = e.pageX - container.offsetLeft;
      scrollLeft = container.scrollLeft;
    });

    container.addEventListener('mouseleave', () => {
      isDown = false;
      container.classList.remove('grabbing');
    });

    container.addEventListener('mouseup', () => {
      isDown = false;
      container.classList.remove('grabbing');
    });

    container.addEventListener('mousemove', (e) => {
      if (!isDown) return;
      e.preventDefault();
      const x = e.pageX - container.offsetLeft;
      const walk = (x - startX) * 2; // Adjust scrolling speed
      container.scrollLeft = scrollLeft - walk;
    });
  }

  initializeTableScroll() {
    const tableWrapper = document.querySelector('.products-table-wrapper');
    if (tableWrapper) {
      tableWrapper.addEventListener('scroll', () => {
        if (tableWrapper.scrollLeft > 0) {
          tableWrapper.classList.add('is-scrolled');
        } else {
          tableWrapper.classList.remove('is-scrolled');
        }
      });
    }
  }

  // Add new method to handle JSON metafields
  getJsonMetafieldValue(product, key) {
    try {
      const metafield = product.metafields?.find((field) => field?.key === key);

      if (!metafield?.value) {
        return '-';
      }

      const jsonValue = JSON.parse(metafield.value);

      // The value might be directly in the JSON, not in a 'value' property
      return jsonValue?.value || jsonValue || '-';
    } catch (error) {
      return '-';
    }
  }

  initializeMetricBars() {
    const fills = document.querySelectorAll('.bar-fill');

    fills.forEach((fill) => {
      const value = parseInt(fill.getAttribute('data-value'), 10);

      // Set width based on value
      fill.style.width = `${value}%`;

      // Change bar color based on value
      let color;
      if (value < 21) {
        color = '#e1a065';
      } else if (value < 31) {
        color = '#cfb55e';
      } else if (value < 61) {
        color = '#a0b861';
      } else if (value < 81) {
        color = '#769a46';
      } else {
        color = '#189332';
      }

      fill.style.backgroundColor = color;
    });
  }

  // Update labels with translations
  updateLabels() {
    // Helper function to get translation
    const getTranslation = (key, fallback) => {
      try {
        // First try to get from window.theme.strings
        if (window.theme?.strings?.board_finder?.[key]) {
          return window.theme.strings.board_finder[key];
        }

        // If not found, try to get from Shopify.locale
        const currentLocale = Shopify.locale || 'en';

        // Return appropriate fallback based on locale
        const englishTranslations = {
          rider_length: 'The Rider Length',
          boot_size: 'Boot Size',
          show_snowboards: 'Show Snowboards',
          riding_type: 'Type of Riding',
          brand: 'Brand',
        };

        return currentLocale === 'en' ? englishTranslations[key] || fallback : fallback;
      } catch (error) {
        console.warn('Error getting translation:', error);
        return fallback;
      }
    };

    // Get translation elements
    const elements = {
      riderLength: document.querySelector('label[for="rider-length"]'),
      bootSize: document.querySelector('label[for="boot-size"]'),
      findBoards: document.getElementById('find-boards'),
      ridingType: document.querySelector('.board-style-filter label'),
      brand: document.querySelector('.vendor-filter label'),
    };

    // Update each element if it exists
    if (elements.riderLength) {
      elements.riderLength.textContent = getTranslation('rider_length', 'Åkarens längd');
    }
    if (elements.bootSize) {
      elements.bootSize.textContent = getTranslation('boot_size', 'Storlek på Boots');
    }
    if (elements.findBoards) {
      elements.findBoards.textContent = getTranslation('show_snowboards', 'Visa Snowboards');
    }
    if (elements.ridingType) {
      elements.ridingType.textContent = getTranslation('riding_type', 'Typ av åkning');
    }
    if (elements.brand) {
      elements.brand.textContent = getTranslation('brand', 'Varumärke');
    }

    // Style mapping for translations
    const styleTranslationMap = {
      Freestyle: 'freestyle',
      'All Mountain': 'all_mountain',
      Puder: 'puder',
      Carving: 'carving',
      Kids: 'kids',
    };

    // Update riding style labels
    const styleSlides = document.querySelectorAll('.board-style-slide span');

    styleSlides.forEach((slide) => {
      const style = slide.closest('.swiper-slide')?.getAttribute('data-style');

      if (style) {
        const translationKey = styleTranslationMap[style];

        if (translationKey && window.theme?.strings?.board_finder?.styles?.[translationKey]) {
          slide.textContent = window.theme.strings.board_finder.styles[translationKey];
        }
      }
    });
  }

  updateURLParams() {
    const params = new URLSearchParams();

    params.set('riderLength', this.riderLengthInput.value);
    params.set('bootSize', this.bootSizeInput.value);
    params.set('layout', this.currentView);

    if (this.selectedBoardStyles.length > 0) {
      params.set('style', this.selectedBoardStyles[0]);
    }

    const selectedVendors = Array.from(this.vendorWrapper.querySelectorAll('.vendor-button.active'))
      .map(btn => btn.dataset.vendor);
    
    if (selectedVendors.length > 0) {
      params.set('vendors', selectedVendors.join(','));
    }
    
    const newUrl = `${window.location.pathname}?${params.toString()}`;
    history.pushState({ path: newUrl }, '', newUrl);
  }

  initializeFromURL() {
    const params = new URLSearchParams(window.location.search);

    const riderLength = params.get('riderLength');
    const bootSize = params.get('bootSize');
    const style = params.get('style');
    const view = params.get('layout');
    const vendors = params.get('vendors');

    let hasFilters = false;

    if (riderLength && this.riderLengthInput) {
      this.riderLengthInput.value = riderLength;
      hasFilters = true;
    }

    if (bootSize && this.bootSizeInput) {
      this.bootSizeInput.value = bootSize;
      hasFilters = true;
    }

    if (style) {
      this.selectedBoardStyles = [style];
      hasFilters = true;
    }

    if (view) {
      this.currentView = view;
    }
    
    if (vendors) {
        this.initialVendors = vendors.split(',');
    }
    
    return hasFilters;
  }
}

// Initialize the board finder
document.addEventListener('DOMContentLoaded', () => {
  console.log('DOM loaded, initializing BoardFinder');
  new BoardFinder();
});

// Add this to handle view switching
document.querySelectorAll('.view-button').forEach((button) => {
  button.addEventListener('click', function () {
    const view = this.dataset.view;
    const productsGrid = document.querySelector('.products-grid');
    const productsList = document.querySelector('.products-list');

    // Update active button state
    document.querySelectorAll('.view-button').forEach((btn) => btn.classList.remove('active'));
    this.classList.add('active');

    if (view === 'list') {
      productsGrid.classList.add('hidden');
      productsList.classList.remove('hidden');
    } else {
      productsGrid.classList.remove('hidden');
      productsList.classList.add('hidden');
    }
  });
});
